import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import jsPDF from 'jspdf';
import { FiCamera, FiTrash2, FiFilePlus, FiSend, FiLoader, FiCheckCircle, FiPower, FiX } from 'react-icons/fi';
import './ScanScreen.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ScanScreen = ({ onLogout }) => { // Récupère onLogout depuis les props
  const webcamRef = useRef(null);
  const [images, setImages] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const startScan = () => {
    setIsScanning(true);
    setSuccess(false);
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      setImages(prevImages => [...prevImages, imageSrc]);
      setIsScanning(false); // On quitte le mode scan pour montrer l'aperçu
    }
  }, [webcamRef]);

  const handleDeletePage = (index) => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (images.length === 0) {
      alert('Veuillez prendre au moins une photo.');
      return;
    }
    setLoading(true);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Crée une promesse pour chaque image à charger
    const imagePromises = images.map(imgSrc => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = imgSrc;
            img.onload = () => resolve(img);
            img.onerror = (err) => reject(err);
        });
    });

    Promise.all(imagePromises)
        .then(loadedImages => {
            // Toutes les images sont chargées, on peut les ajouter au PDF
            loadedImages.forEach((img, index) => {
                if (index > 0) {
                    pdf.addPage();
                }
                const imgProps = pdf.getImageProperties(img);
                const ratio = Math.min(pdfWidth / imgProps.width, pdfHeight / imgProps.height);
                const imgWidth = imgProps.width * ratio;
                const imgHeight = imgProps.height * ratio;
                const x = (pdfWidth - imgWidth) / 2;
                const y = (pdfHeight - imgHeight) / 2;
                pdf.addImage(img, 'JPEG', x, y, imgWidth, imgHeight);
            });

            // Une fois toutes les images ajoutées, on génère le PDF et on envoie
            const pdfBlob = pdf.output('blob');
            const formData = new FormData();
            formData.append('document', pdfBlob, 'document.pdf');
            // Le champ 'to' est supprimé, le serveur s'en chargera
            formData.append('subject', 'Nouveau document scanné');
            formData.append('body', 'Veuillez trouver ci-joint le document scanné.');
            
            // Logique de récupération de l'ID et du nom corrigée
            const driverUser = JSON.parse(localStorage.getItem('driverUser'));
            const driverName = driverUser ? driverUser.name : 'Chauffeur Inconnu';
            const driverId = driverUser ? driverUser.id : null;

            formData.append('driverName', driverName);
            formData.append('driverId', driverId);

            return fetch(`${API_URL}/api/send-email`, {
                method: 'POST',
                body: formData,
            });
        })
        .then(response => response.text())
        .then(data => {
            console.log(data);
            setLoading(false);
            setSuccess(true);
            setTimeout(() => {
              setSuccess(false);
              setIsScanning(false);
              setImages([]);
            }, 2000);
        })
        .catch(error => {
            console.error('Error sending document:', error);
            setLoading(false);
            alert("Erreur lors de la création du PDF ou de l'envoi du document.");
        });
  };
  
  // ... (le reste de vos fonctions : capture, handleDeletePage, handleSend, etc. restent inchangées)

  const renderContent = () => {
    if (success) {
        return (
          <div className="success-message fade-in">
            <FiCheckCircle size={60} />
            <h2>Envoi réussi !</h2>
            <p>Le document a été envoyé avec succès.</p>
          </div>
        );
      }
  
      if (loading) {
        return (
          <div className="loading-container fade-in">
            <FiLoader className="loader-icon" size={60} />
            <h2>Envoi en cours...</h2>
          </div>
        );
      }

    if (isScanning) {
      return (
        <div className="webcam-container fade-in">
          <button onClick={() => setIsScanning(false)} className="close-webcam-btn">
            <FiX />
          </button>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            width="100%"
            videoConstraints={{
                facingMode: "environment"
            }}
            className="webcam-video"
          />
           <div className="button-group">
             <button onClick={capture} className="capture-btn">
                <FiCamera size={24}/>
                <span>Prendre la photo</span>
            </button>
           </div>
        </div>
      );
    }
    
    if (images.length > 0) {
        return (
          <div className="preview-container fade-in">
              <h2>Aperçu ({images.length} page{images.length > 1 ? 's' : ''})</h2>
              <div className="image-grid">
                  {images.map((imgSrc, index) => (
                      <div key={index} className="image-preview-item">
                          <img src={imgSrc} alt={`Page ${index + 1}`} />
                          <button onClick={() => handleDeletePage(index)} className="delete-page-btn">
                              <FiTrash2 />
                          </button>
                      </div>
                  ))}
                   <button onClick={startScan} className="add-page-btn">
                      <FiFilePlus size={24} />
                      <span>Ajouter une page</span>
                  </button>
              </div>
              <div className="button-group">
                  <button onClick={handleSend} className="send-btn" disabled={loading}>
                      <FiSend size={24} />
                      <span>Envoyer</span>
                  </button>
              </div>
          </div>
        );
      }
  
      return (
          <div className="initial-container">
              <button onClick={startScan} className="scan-button">
                  <FiCamera size={50} />
                  <span>SCANNER</span>
              </button>
          </div>
      );
  };

  return (
    <div className="scan-screen">
      <button onClick={onLogout} className="logout-btn">
        <FiPower title="Déconnexion" />
      </button>
      {renderContent()}
    </div>
  );
};

export default ScanScreen;
